import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
/**
 * ERPs State
 * 
 * Contains all state for the ERPs subdomain.
 * Similar structure to Channels but simpler.
 */

export interface AddERPFormData {
  name: string;
  apiKey: string;
  apiSecret: string;
}

export interface ERPDetailFormData {
  label: string;
}

/**
 * A primitive value as it comes back from the server. Drift values are compared
 * by the server, never here — the FE only renders what it was told.
 */
export type DriftValue = string | number | boolean | null;

/**
 * One row of the drift audit table (TV1-2146): a render-time LEFT JOIN of one
 * Drift Episode onto its ERP Sync Mirror row. FE-only view type — it is never
 * persisted and nothing here is computed by us (ADR-069 D5).
 *
 * THE HAZARD (SDD Risk R1): there are THREE values and TWO clocks, and they are
 * deliberately kept as separate fields.
 *   - `ourValueAtOpen` / `theirValueAtOpen` come FROM THE EPISODE and are a
 *     snapshot of the moment the divergence was OPENED.
 *   - `theirValueLastObserved` comes FROM THE MIRROR and is the LAST value the
 *     ERP was observed to hold, which may be newer than the episode and may
 *     already agree with us.
 * Collapsing "theirs at open" into "theirs now" would silently show an operator
 * a stale disagreement as a live one (or vice versa), so they must never be
 * merged into a single field or a single column.
 *
 * Likewise the two clocks are distinct: `observedAt` is when WE observed the
 * ERP, `writeDate` is when the ERP itself says the record was last written.
 * TV1-2132 §3c is exactly the gap between them, so merging them into one
 * "last updated" would erase the very signal this view exists to show.
 */
export interface DriftAuditRow {
  episodeId: string;
  syncKey: string;
  entity: string;
  field: string;
  itemId?: string;
  /** Resolved label if the item read succeeded; absent ⇒ render the raw itemId. */
  itemLabel?: string;
  /** FROM THE EPISODE — Teselly's value at the moment the episode opened. */
  ourValueAtOpen?: DriftValue;
  /** FROM THE EPISODE — the ERP's value at the moment the episode opened. */
  theirValueAtOpen?: DriftValue;
  /** FROM THE MIRROR — the last value observed in the ERP. NOT the at-open value. */
  theirValueLastObserved?: DriftValue;
  /** Mirror clock: when WE last observed the ERP. */
  observedAt?: string;
  /** ERP clock: when the ERP says the record was last written. */
  writeDate?: string;
  /** Scope, straight from the mirror row — never parsed out of the syncKey. */
  warehouseId?: string;
  priceListId?: string;
  /** Provenance, straight from the mirror row. */
  externalProductId?: string;
  externalScopeId?: string;
  currencyIsoCode?: string;
  openedAt: string;
  closedAt?: string;
  durationMs?: number;
  status: 'open' | 'closed';
  /**
   * True when no mirror row matched this syncKey. Explicit rather than inferred
   * from undefined values, so the table can render the raw syncKey instead of
   * fabricating a scope label.
   */
  mirrorMissing: boolean;
}

/** Filter of the drift audit panel. `all` means "do not filter on this axis". */
export interface DriftAuditFilter {
  entity: 'all' | 'item' | 'stock' | 'price';
  status: 'open' | 'closed' | 'all';
}

export interface ERPsState {
  /** ERPs data */
  erps: Record<string, SystemIntegrationsReadmodel>;

  /** ERP detail view */
  erpDetail: {
    mode: { type: 'closed' } | { type: 'view'; erpId: string };
    /**
     * Selected tab of the ERP configuration view. A plain string, NOT a literal
     * union, because the registry (`ERPInfo/erpInfoTabs.tsx`) is the single
     * source of truth for which tabs exist — a new tab is registered there and
     * nowhere else. The view resolves an unknown value back to the default tab,
     * so an unregistered `?tab=` can never render a blank panel.
     */
    activeTab: string;
    isEditing: boolean;
    formData: ERPDetailFormData;
    formErrors: Record<string, string>;
  };

  /** Add ERP modal */
  addERPModal: {
    isOpen: boolean;
    step: 'selectType' | 'enterDetails' | 'connecting';
    selectedType: string | null;
    formData: AddERPFormData;
    formErrors: Record<string, string>;
  };

  /** Delete confirmation modal */
  deleteModal: {
    isOpen: boolean;
    erpId: string | null;
  };

  /** OAuth redirect handling */
  oauthRedirect: {
    isProcessing: boolean;
    code: string | null;
    state: string | null;
  };

  /** Loading states */
  loading: {
    erpsLoading: boolean;
    submitting: boolean;
    deleting: boolean;
  };

  /**
   * Drift audit panel (TV1-2146). Purely a VIEW of two server-owned facts — the
   * Drift Episode time-series and the ERP Sync Mirror. Nothing derived is ever
   * persisted from here (ADR-069 D5); `rows` is a render-time join, discarded on
   * close.
   */
  driftAudit: {
    /** Episode ⟕ mirror join rows for the current page, in server sort order. */
    rows: DriftAuditRow[];
    /** Total matching episodes, from the search response `count`. */
    total: number;
    filter: DriftAuditFilter;
    /** 1-based. */
    page: number;
    loading: boolean;
    /** syncKeys with an in-flight re-check, so the row's button can spin. */
    rechecking: string[];
    /**
     * Last re-check OUTCOME per syncKey (TV1-2146, SDD Risk R2). This is the
     * outcome of the operator's own act — "the ERP answered", "the product is
     * gone", "this entity cannot be point-read" — never row data from the
     * response (decision D6): the row's values still refresh only through the
     * episodes subscription.
     */
    recheckOutcomes: Record<string, DriftRecheckOutcome>;
  };
}

/**
 * Tab the detail view starts on. The view re-resolves whatever is stored here
 * against the live tab registry and falls back to the FIRST registered tab, so
 * this is a seed, never an authority.
 */
export const DEFAULT_ERP_DETAIL_TAB = 'general';

/**
 * The panel opens on what an operator actually needs to act on: every entity,
 * but only the divergences that are still OPEN. Closed episodes stay reachable
 * via the status filter as history.
 */
/**
 * What a per-row re-check concluded. `observed` means the ERP answered and the
 * observation was recorded server-side; the row itself refreshes through the
 * episodes subscription, never from the response.
 */
export type DriftRecheckOutcome = 'observed' | 'not-found' | 'unsupported-entity' | 'failed';

export const DEFAULT_DRIFT_FILTER: DriftAuditFilter = { entity: 'all', status: 'open' };

/** Page size of the episodes search. One mirror query per page, never per row. */
export const DRIFT_PAGE_SIZE = 50;

const createInitialFormData = (): AddERPFormData => ({
  name: '',
  apiKey: '',
  apiSecret: ''
});

const createInitialDetailFormData = (): ERPDetailFormData => ({
  label: ''
});

export const initialState: ERPsState = {
  erps: {},

  erpDetail: {
    mode: { type: 'closed' },
    activeTab: DEFAULT_ERP_DETAIL_TAB,
    isEditing: false,
    formData: createInitialDetailFormData(),
    formErrors: {}
  },

  addERPModal: {
    isOpen: false,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  },

  deleteModal: {
    isOpen: false,
    erpId: null
  },

  oauthRedirect: {
    isProcessing: false,
    code: null,
    state: null
  },

  loading: {
    erpsLoading: false,
    submitting: false,
    deleting: false
  },

  driftAudit: {
    rows: [],
    total: 0,
    // Spread, never alias: `DEFAULT_DRIFT_FILTER` is a module-level const, so sharing
    // the reference would let any future reducer that mutates in place corrupt the
    // default for every subsequent reset.
    filter: { ...DEFAULT_DRIFT_FILTER },
    page: 1,
    loading: false,
    rechecking: [],
    recheckOutcomes: {}
  }
};

export { createInitialFormData, createInitialDetailFormData };

export const selectors = {
  getERP: ( state: ERPsState, erpId: string ) =>
    state.erps[erpId],

  erpsList: ( state: ERPsState ) =>
    Object.values( state.erps ),

  currentERP: ( state: ERPsState ) => {
    if ( state.erpDetail.mode.type === 'closed' ) return null;
    return state.erps[state.erpDetail.mode.erpId];
  },

  activeTab: ( state: ERPsState ) =>
    state.erpDetail.activeTab,

  isAddModalOpen: ( state: ERPsState ) =>
    state.addERPModal.isOpen,

  isDeleteModalOpen: ( state: ERPsState ) =>
    state.deleteModal.isOpen,

  isSubmitting: ( state: ERPsState ) =>
    state.loading.submitting,

  driftRows: ( state: ERPsState ) =>
    state.driftAudit.rows,

  driftTotal: ( state: ERPsState ) =>
    state.driftAudit.total,

  driftFilter: ( state: ERPsState ) =>
    state.driftAudit.filter,

  driftPage: ( state: ERPsState ) =>
    state.driftAudit.page,

  driftLoading: ( state: ERPsState ) =>
    state.driftAudit.loading,

  /**
   * Whether a re-check is in flight for this syncKey. Keyed by syncKey rather
   * than by row index because the row list is replaced wholesale on every
   * refresh, and a re-check outlives the page it was started from.
   */
  isRechecking: ( state: ERPsState, syncKey: string ) =>
    state.driftAudit.rechecking.includes( syncKey ),

  /**
   * The last re-check outcome for this syncKey, or undefined if it has not been
   * re-checked in this session. Keyed by syncKey for the same reason
   * `isRechecking` is: the row list is replaced wholesale on every refresh.
   */
  recheckOutcome: ( state: ERPsState, syncKey: string ): DriftRecheckOutcome | undefined =>
    state.driftAudit.recheckOutcomes[syncKey]
};
